import {
  column,
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  multiFilter,
  numericColumn,
  simpleHeader,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useMemo } from "react";
import { MarkupSummary, Price } from "src/components";
import { ReviewImpactedPrice } from "../../components/ReviewImpactedPrice";
import { ReviewProjectImpact_CostCodeFragment } from "../ReviewImpactStep.generated";

/** One row of the Project Budget table, i.e. what a single cost code costs today vs. after the configuration. */
type ProjectBudgetRow = {
  costCode: ReviewProjectImpact_CostCodeFragment;
  currentBudgetInCents: number;
  /** The change event's cost delta for the cost code, so the new budget is `current + change`. */
  budgetChangeInCents: number;
  newPriceInCents: number;
};

type ProjectBudgetTableProps = { budgetRows: ProjectBudgetRow[] };

/** The per-cost-code "what the project's budget looks like before and after this configuration" table. */
export function ProjectBudgetTable(props: ProjectBudgetTableProps) {
  const { budgetRows } = props;

  const filterDefs: FilterDefs<ProjectBudgetFilter> = useMemo(() => {
    const costCodeIds = budgetRows
      .map((r) => r.costCode)
      .unique()
      .sortByKey("displayName");
    return {
      costCodeIds: multiFilter({
        label: "Cost Code",
        options: costCodeIds,
        getOptionLabel: ({ displayName }) => displayName,
        getOptionValue: ({ id }) => id,
      }),
      showChanges: toggleFilter({ label: "Only Show Changes", defaultValue: false }),
    };
  }, [budgetRows]);

  const layoutState = useGridTableLayoutState<ProjectBudgetFilter>({
    persistedFilter: { filterDefs, storageKey: "reviewImpactBudgetFilter" },
    search: "client",
  });
  const { filter } = layoutState;

  // The totals row is always the whole configuration's impact, even while the table is filtered down
  const rows = useMemo(() => createRows(budgetRows, filter), [budgetRows, filter]);

  return (
    <GridTableLayout
      layoutState={layoutState}
      hideEditColumns
      tableProps={{ id: "projectBudgetTable", rows, columns }}
    />
  );
}

function createRows(rows: ProjectBudgetRow[], filter: ProjectBudgetFilter): GridDataRow<Row>[] {
  return [
    simpleHeader,
    {
      kind: "totals" as const,
      id: "totals",
      data: {
        currentBudgetInCents: rows.sum((r) => r.currentBudgetInCents),
        budgetChangeInCents: rows.sum((r) => r.budgetChangeInCents),
        newPriceInCents: rows.sum((r) => r.newPriceInCents),
      },
    },
    ...rows
      .filter(({ costCode }) => !filter.costCodeIds?.nonEmpty || filter.costCodeIds.includes(costCode.id))
      .filter(({ budgetChangeInCents }) => (filter.showChanges ? budgetChangeInCents !== 0 : true))
      .map((r) => ({ kind: "costCode" as const, id: r.costCode.id, data: r })),
  ];
}

type ProjectBudgetFilter = { costCodeIds?: string[]; showChanges?: boolean };

/** The totals row, i.e. what every cost code in the configuration adds up to. */
type ProjectBudgetTotals = Omit<ProjectBudgetRow, "costCode">;

type Row =
  | { kind: "totals"; id: string; data: ProjectBudgetTotals }
  | { kind: "header"; id: string }
  | { kind: "costCode"; id: string; data: ProjectBudgetRow };

const columns: GridColumn<Row>[] = [
  column<Row>({
    totals: () => ({ content: "Total", css: Css.smSb.$ }),
    header: "Cost Code",
    costCode: ({ costCode }) => costCode.displayName,
    w: 2,
  }),
  numericColumn<Row>({
    totals: ({ currentBudgetInCents }) => <Price valueInCents={currentBudgetInCents} />,
    header: "Current Budget",
    costCode: ({ currentBudgetInCents }) => <Price valueInCents={currentBudgetInCents} />,
  }),
  numericColumn<Row>({
    totals: ({ currentBudgetInCents, budgetChangeInCents }) => (
      <Price valueInCents={currentBudgetInCents + budgetChangeInCents} />
    ),
    header: "New Budget",
    costCode: ({ currentBudgetInCents, budgetChangeInCents }) => (
      <Price valueInCents={currentBudgetInCents + budgetChangeInCents} />
    ),
  }),
  numericColumn<Row>({
    totals: ({ budgetChangeInCents }) => <ReviewImpactedPrice valueInCents={budgetChangeInCents} />,
    header: "Budget Change",
    costCode: ({ budgetChangeInCents }) => <ReviewImpactedPrice valueInCents={budgetChangeInCents} />,
  }),
  numericColumn<Row>({
    totals: ({ budgetChangeInCents, newPriceInCents }) => (
      <MarkupSummary budget={budgetChangeInCents} price={newPriceInCents} />
    ),
    header: "Markup",
    costCode: ({ budgetChangeInCents, newPriceInCents }) => (
      <MarkupSummary budget={budgetChangeInCents} price={newPriceInCents} />
    ),
  }),
  numericColumn<Row>({
    totals: ({ newPriceInCents }) => <ReviewImpactedPrice valueInCents={newPriceInCents} />,
    header: "Price",
    costCode: ({ newPriceInCents }) => <ReviewImpactedPrice valueInCents={newPriceInCents} />,
  }),
];
